// 项目群成员规则 / 补建项目群 / 清理无关成员（admin）。
//
// 需求（2026-10-21 用户反馈）：「系统自动建群会拉无关人员进来，按原逻辑收紧，只拉对应项目的相关人员，
// 并区分设计项目 / 工程项目 —— 现在系统里很多工程项目基本是全员都在。」
//
// 本文件对应三件事：
//   ① 成员规则：设计侧 / 工程侧两套，分开配置（旧版是「建项即拉财务 / 总部 / 全部销售副总 / 全部设计部经理」，
//      正是全员群的来源，已从默认值移除）；
//   ② 补建项目群：给尚无群的在编项目补建（沿用原有能力，不变）；
//   ③ 清理无关成员：按新规则把历史群里的旧规则成员移出（预览 → 执行两步，避免误删）。
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, FolderPlus, Save, RotateCcw, Users, UserMinus, ShieldCheck } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";

/* 设计侧规则：用于设计项目群（dproj:*）与尚未开工的工程项目群。 */
const DESIGN_GROUPS = [
  ["designRoles", "设计侧 · 跨分公司全部拉入的岗位", "Design · roles added from all branches", "role"],
  ["designBranchRoles", "设计侧 · 只拉本项目所在分公司的岗位", "Design · roles added from the project's branch only", "role"],
  ["designDepts", "设计侧 · 跨分公司全部拉入的部门", "Design · departments added from all branches", "dept"],
];

/* 工程侧规则：用于已进入施工的工程项目群。 */
const CONSTRUCTION_GROUPS = [
  ["constructionRoles", "工程侧 · 跨分公司全部拉入的岗位", "Construction · roles added from all branches", "role"],
  ["constructionBranchRoles", "工程侧 · 只拉本项目所在分公司的岗位（该分公司没有则退回全部）", "Construction · project's branch only (fallback: all)", "role"],
  ["constructionDepts", "工程侧 · 跨分公司全部拉入的部门", "Construction · departments added from all branches", "dept"],
];

const CheckGrid = ({ tid, options, value, onChange }) => (
  <div className="flex flex-wrap gap-1.5" data-testid={tid}>
    {options.map((o) => {
      const on = value.includes(o.id);
      return <button type="button" key={o.id} data-testid={`${tid}-${o.id}`} data-on={on ? 1 : 0} onClick={() => onChange(on ? value.filter((x) => x !== o.id) : [...value, o.id])}
        className={"max-md:min-h-11 max-md:min-w-11 " + (`text-[11px] rounded-full border px-2.5 py-1 transition-colors ${on ? "border-[#4F6BFF] bg-indigo-50 text-[#4F6BFF] font-semibold" : "border-slate-200 text-slate-600 hover:border-indigo-300"}`)}>{o.label}</button>;
    })}
  </div>
);

const Section = ({ title, hint, children }) => (
  <section className="rounded-xl border border-slate-200 px-3 py-2.5">
    <h4 className="text-xs font-semibold text-slate-800">{title}</h4>
    {hint && <p className="mt-0.5 text-[11px] text-slate-500">{hint}</p>}
    <div className="mt-2 space-y-3">{children}</div>
  </section>
);

/* admin：项目群自动成员规则（保存即对所有进行中项目群补齐成员，只加不删）+ 补建项目群 + 清理无关成员 */
export const ProjectGroupRuleDialog = ({ api, zh, onClose, initialTab = "rule" }) => {
  const T = (z, e) => (zh ? z : e);
  const [tab, setTab] = useState(initialTab);
  const [rule, setRule] = useState(null);
  const [def, setDef] = useState(null);
  const [opts, setOpts] = useState({ role: [], dept: [], region: [] });
  const [meta, setMeta] = useState({});
  const [saving, setSaving] = useState(false);
  const [bf, setBf] = useState(null);
  const [bfBusy, setBfBusy] = useState(false);
  const [cl, setCl] = useState(null);          // 清理预览结果
  const [clBusy, setClBusy] = useState(false);
  const [clKind, setClKind] = useState("");    // "" 全部 / design / project
  const err = (e) => toast.error(e?.response?.data?.detail || e.message);
  const loadBf = () => api.get("/chat/admin/backfill-project-groups").then(({ data }) => setBf(data)).catch(err);
  const loadCleanup = () => api.get("/chat/admin/project-group-members/cleanup").then(({ data }) => setCl(data)).catch(err);
  useEffect(() => {
    Promise.all([api.get("/chat/admin/project-group-rule"), api.get("/roles"), api.get("/branches")]).then(([r, ro, br]) => {
      setRule(r.data.rule); setDef(r.data.default); setMeta({ updatedAt: r.data.updatedAt, updatedBy: r.data.updatedBy });
      const roles = (ro.data.builtin || ro.data.roles || []).filter((x) => x.key !== "subcontractor").map((x) => ({ id: x.key, label: zh ? x.zh : x.en }));
      setOpts({ role: roles, dept: (r.data.depts || []).map((d) => ({ id: d.id, label: zh ? d.zh : d.en })), region: [{ id: "hq", label: T("总部 HQ", "HQ") }, ...(br.data || []).filter((b) => b.id !== "hq").map((b) => ({ id: b.id, label: zh ? b.zh : b.en }))] });
    }).catch(err);
    loadBf();
    loadCleanup();
  }, []); // eslint-disable-line
  useEffect(() => {
    if (!bf?.last?.running) return undefined;
    const t = setInterval(loadBf, 3000);
    return () => clearInterval(t);
  }, [bf?.last?.running]); // eslint-disable-line
  const save = async () => {
    setSaving(true);
    try { const { data } = await api.put("/chat/admin/project-group-rule", rule); setRule(data.rule); toast.success(T(`规则已保存 · 已补齐 ${data.synced} 个进行中项目群（只加人不删人）`, `Saved · synced ${data.synced} active groups`)); setMeta({ updatedAt: new Date().toISOString(), updatedBy: T("我", "me") }); }
    catch (e) { err(e); }
    setSaving(false);
  };
  const run = async () => {
    setBfBusy(true);
    try { const { data } = await api.post("/chat/admin/backfill-project-groups"); toast.success(data.started ? T(`已开始后台补建：工程部 ${data.engineering} 个 · 设计部 ${data.design} 个，完成后铃铛通知`, `Started: ${data.engineering} + ${data.design} groups`) : T("没有需要补建的项目", "Nothing to backfill")); setTimeout(loadBf, 800); }
    catch (e) { err(e); }
    setBfBusy(false);
  };
  const doCleanup = async () => {
    const n = clKind ? (cl?.removeByKind?.[clKind] ?? 0) : (cl?.totalRemove || 0);
    const msg = T(`将把 ${n} 位与本项目无关的成员移出${clKind === "design" ? "设计项目群" : clKind === "project" ? "工程项目群" : "项目群"}。\n群主、手动邀请的人、仍在本项目的人不会被移出。\n移出后如需恢复，群主可重新邀请。确认执行？`,
      `Remove ${n} unrelated members? Owners and manually invited people are kept.`);
    if (!window.confirm(msg)) return;
    setClBusy(true);
    try {
      const { data } = await api.post("/chat/admin/project-group-members/cleanup", clKind ? { kind: clKind } : {});
      toast.success(T(`已清理完成 · 涉及 ${data.groups.design + data.groups.project} 个群 · 共移出 ${data.removed} 人`, `Done · ${data.removed} members removed`));
      loadCleanup();
    } catch (e) { err(e); }
    setClBusy(false);
  };
  const tabBtn = (k, l, tid) => <button type="button" data-testid={tid} data-on={tab === k ? 1 : 0} onClick={() => setTab(k)} className={"max-md:min-h-11 max-md:min-w-11 " + (`px-3 py-1.5 text-xs font-semibold rounded-md ${tab === k ? "bg-white shadow text-slate-900" : "text-slate-500"}`)}>{l}</button>;
  const groupRow = ([k, zl, el, kind]) => (
    <div key={k} data-testid={`pgr-group-${k}`}>
      <div className="flex items-center justify-between mb-1"><span className="text-xs font-semibold text-slate-700">{T(zl, el)}</span><span className="text-[10px] text-slate-400">{(rule[k] || []).length} {T("项", "selected")}</span></div>
      <CheckGrid tid={`pgr-${k}`} options={opts[kind]} value={rule[k] || []} onChange={(v) => setRule((r) => ({ ...r, [k]: v }))} />
    </div>
  );
  const clCount = (kind) => (cl?.byKind?.[kind] ?? 0);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="pgr-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Users className="h-4 w-4 text-[#4F6BFF]" />{T("项目群成员规则 / 补建 / 清理", "Project group rules / backfill / cleanup")}</DialogTitle></DialogHeader>
        <div className="inline-flex rounded-lg bg-slate-100 p-0.5 w-fit">{tabBtn("rule", T("成员规则", "Member rule"), "pgr-tab-rule")}{tabBtn("backfill", T("补建项目群", "Backfill groups"), "pgr-tab-backfill")}{tabBtn("cleanup", T("清理无关成员", "Clean up"), "pgr-tab-cleanup")}</div>
        {tab === "rule" && (!rule ? <div className="py-8 text-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin inline" /></div> : (
          <div className="space-y-4" data-testid="pgr-rule">
            <p className="text-[11px] text-slate-600 rounded-xl bg-slate-50 border border-slate-200 px-3 py-2" data-testid="pgr-scope-note">
              {T("项目自身负责人始终拉入（负责销售 · 美国 / 中国建筑设计师 · 营销方案设计师 · 项目经理 / 线上项目经理及其协办 · 客服），管理员 / CEO 也始终拉入（保证能收到推送、有未读角标、可被 @），均不可关闭。财务 / 总部 / 销售副总 / 设计部经理默认不再自动拉入 —— 这些是「全公司岗位」，正是之前每个群二十几人的原因；确需某类岗位统一入群时在下面按侧勾选。",
                "Project people (sales · US/CN architects · marketing designer · PM / online PM + co-staff · CS) and admin / CEO are always added. Company-wide roles (finance / HQ / sales VPs / design managers) are no longer added by default.")}
            </p>
            <Section title={T("设计侧 · 设计项目群 / 尚未开工的工程项目群", "Design side · design groups & pre-construction")}
              hint={T("签约前后由设计部在编的阶段。默认只拉本分公司设计经理与市场经理。", "Default: design manager + marketing manager from the project's branch.")}>
              {DESIGN_GROUPS.map(groupRow)}
            </Section>
            <Section title={T("工程侧 · 已进入施工的工程项目群", "Construction side · groups in construction")}
              hint={T("默认只拉本分公司工程经理；项目未填线上项目经理时再拉全部 CDC（下方开关）。", "Default: eng manager from the project's branch; plus all CDC when no online PM is set.")}>
              {CONSTRUCTION_GROUPS.map(groupRow)}
              <label className="flex items-center gap-2 text-xs text-slate-700" data-testid="pgr-cdc-fallback-row">
                <input type="checkbox" data-testid="pgr-cdc-fallback" checked={!!rule.cdcFallback} onChange={(e) => setRule((r) => ({ ...r, cdcFallback: e.target.checked }))} className="h-3.5 w-3.5" />
                {T("工程阶段若项目未填「线上项目经理」→ 拉入全部 CDC（线上项目经理）", "Construction stage: if no online PM is set → add all CDC accounts")}
              </label>
            </Section>
            <div className="flex items-center gap-2 pt-1">
              <Button data-testid="pgr-save" disabled={saving} onClick={save} className="bg-[#4F6BFF] hover:bg-[#3f58e0] text-white">{saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}{T("保存并补齐进行中项目群", "Save & sync active groups")}</Button>
              <Button data-testid="pgr-reset" variant="outline" onClick={() => setRule({ ...def })}><RotateCcw className="h-4 w-4 mr-1" />{T("恢复系统默认", "Reset to default")}</Button>
              {meta.updatedAt && <span className="text-[10px] text-slate-400 ml-auto" data-testid="pgr-updated">{T("上次保存", "Saved")} {new Date(meta.updatedAt).toLocaleString()} · {meta.updatedBy}</span>}
            </div>
            <p className="text-[11px] text-slate-500" data-testid="pgr-prune-hint">
              {T("保存只会「补人」，不会把任何人移出群。要清掉历史群里的旧规则成员（财务 / 总部 / 销售副总等），请到「清理无关成员」标签预览后执行。",
                "Saving only adds members. To remove legacy rule members from existing groups, use the Clean up tab.")}
            </p>
          </div>
        ))}
        {tab === "backfill" && (
          <div className="space-y-3" data-testid="pgr-backfill">
            {!bf ? <div className="py-8 text-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin inline" /></div> : (
              <>
                <div className="grid grid-cols-3 gap-2 text-center">
                  {[["engineering", T("工程部在编 · 待建", "Construction · pending"), "text-amber-700 bg-amber-50 border-amber-200"], ["design", T("设计部在编 · 待建", "Design · pending"), "text-violet-700 bg-violet-50 border-violet-200"], ["existing", T("已有项目群", "Existing groups"), "text-slate-700 bg-slate-50 border-slate-200"]].map(([k, l, cls]) => (
                    <div key={k} data-testid={`pgr-bf-${k}`} data-count={bf[k]} className={`rounded-xl border px-2 py-2.5 ${cls}`}><div className="text-xl font-bold">{bf[k]}</div><div className="text-[10px]">{l}</div></div>
                  ))}
                </div>
                {bf.sample?.length > 0 && <p className="text-[11px] text-slate-500 truncate" data-testid="pgr-bf-sample">{T("示例：", "e.g. ")}{bf.sample.join(" · ")}</p>}
                <p className="text-[11px] text-slate-500 rounded-xl bg-slate-50 border border-slate-200 px-3 py-2">{T("每个群按「成员规则」自动拉人并发一条建群系统消息；设计部在编项目（尚无工程项目）建「设计项目群」，移交工程后自动升级为工程项目群并保留历史。后台执行，完成后给你铃铛通知。", "Each group is created with rule-based members and a system message; design-stage projects get a design group that upgrades on handoff. Runs in background; you get a bell when done.")}</p>
                {bf.last?.running
                  ? <div className="flex items-center gap-2 text-xs text-amber-700" data-testid="pgr-bf-running"><Loader2 className="h-4 w-4 animate-spin" />{T(`正在补建 ${bf.last.total || ""} 个…`, `Creating ${bf.last.total || ""} groups…`)}</div>
                  : bf.last?.finishedAt && <p className="text-[11px] text-emerald-700" data-testid="pgr-bf-last">{T("上次补建", "Last run")} {new Date(bf.last.finishedAt).toLocaleString()} · {T("工程", "eng")} {bf.last.engineering} · {T("设计", "design")} {bf.last.design}{bf.last.failed ? ` · ${T("失败", "failed")} ${bf.last.failed}` : ""} · {bf.last.by}</p>}
                <Button data-testid="pgr-bf-run" disabled={bfBusy || bf.last?.running || (bf.engineering + bf.design) === 0} onClick={run} className="bg-[#4F6BFF] hover:bg-[#3f58e0] text-white">{bfBusy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <FolderPlus className="h-4 w-4 mr-1" />}{T(`为 ${bf.engineering + bf.design} 个在编项目补建项目群`, `Create ${bf.engineering + bf.design} groups`)}</Button>
              </>
            )}
          </div>
        )}
        {tab === "cleanup" && (
          <div className="space-y-3" data-testid="pgr-cleanup">
            {!cl ? <div className="py-8 text-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin inline" /></div> : (
              <>
                <p className="text-[11px] text-slate-600 rounded-xl bg-amber-50 border border-amber-200 px-3 py-2" data-testid="pgr-cl-note">
                  {T("这里清理的是「旧规则自动拉进来、但与本项目无关」的成员（财务 / 总部 HQ / 销售副总 / 设计部经理等）。群主、手动邀请的人、仍在本项目的人一律不动。先预览再执行，执行后可在群成员面板重新邀请。",
                    "Removes members added by the old broad rules who are unrelated to the project. Owners, manually invited people and project members are never touched.")}
                </p>
                <div className="grid grid-cols-3 gap-2 text-center">
                  {[["", T("涉及项目群", "Groups"), cl.groups, "text-slate-700 bg-slate-50 border-slate-200"], ["design", T("设计项目群", "Design"), clCount("design"), "text-violet-700 bg-violet-50 border-violet-200"], ["project", T("工程项目群", "Engineering"), clCount("project"), "text-amber-700 bg-amber-50 border-amber-200"]].map(([k, l, n, cls]) => (
                    <button type="button" key={k || "all"} data-testid={`pgr-cl-kind-${k || "all"}`} data-on={clKind === k ? 1 : 0} onClick={() => setClKind(k)}
                      className={`rounded-xl border px-2 py-2.5 transition-colors ${cls} ${clKind === k ? "ring-2 ring-[#4F6BFF]" : "hover:brightness-[.98]"}`}>
                      <div className="text-xl font-bold">{n}</div><div className="text-[10px]">{l}</div>
                    </button>
                  ))}
                </div>
                <p className="text-[11px] text-slate-500" data-testid="pgr-cl-total">{T("预计移出", "To remove")} <span className="font-semibold text-slate-800">{clKind ? (cl.removeByKind?.[clKind] ?? 0) : cl.totalRemove}</span> {T("人 · 点击上方卡片可按设计 / 工程分别处理", "members")}</p>
                {cl.items?.length > 0 ? (
                  <div className="max-h-64 space-y-1.5 overflow-y-auto pr-1" data-testid="pgr-cl-list">
                    {(cl.items || []).filter((x) => !clKind || x.kind === clKind).slice(0, 20).map((g) => (
                      <div key={g.id} className="rounded-lg border border-slate-200 px-2.5 py-2">
                        <div className="flex items-center justify-between gap-2">
                          <span className="truncate text-xs font-medium text-slate-800">{g.name || g.code}</span>
                          <span className="shrink-0 text-[10px] text-slate-500" data-testid={`pgr-cl-shrink-${g.id}`}>{g.before} → {g.after} {T("人", "members")}</span>
                        </div>
                        <p className="mt-0.5 truncate text-[11px] text-slate-500">{g.remove.map((u) => u.name).join("、")}</p>
                      </div>
                    ))}
                  </div>
                ) : <p className="text-[11px] text-emerald-700" data-testid="pgr-cl-empty">{T("没有需要清理的项目群，当前成员都符合规则。", "Nothing to clean up.")}</p>}
                <div className="flex items-center gap-2">
                  <Button data-testid="pgr-cl-run" disabled={clBusy || !cl.totalRemove} onClick={doCleanup} className="bg-amber-600 hover:bg-amber-700 text-white">
                    {clBusy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <UserMinus className="h-4 w-4 mr-1" />}
                    {T(`清理${clKind === "design" ? "设计项目群" : clKind === "project" ? "工程项目群" : "全部项目群"}无关成员`, "Clean up")}
                  </Button>
                  <Button variant="outline" data-testid="pgr-cl-refresh" onClick={loadCleanup}><ShieldCheck className="h-4 w-4 mr-1" />{T("重新预览", "Refresh")}</Button>
                </div>
              </>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
